import React, { useCallback, useState } from 'react';
import CircleFilled from '@uber/base-icons/circle-filled';
import CircleMinusFilled from '@uber/base-icons/circle-minus-filled';
import CirclePlusFilled from '@uber/base-icons/circle-plus-filled';
import { styled } from 'baseui';
import { Slider } from 'baseui/slider';
import { useAtomValue, useSetAtom } from 'jotai';
import ToolbarButton from '../../../../common/toolbar-button';
import { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { isSAM3EnabledAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { GRID_MODE, MAX_BRUSH_STROKE_SIZE, MIN_BRUSH_STROKE_SIZE, SAM_CLICK, SEGMENTATION_TOOLS } from '../../constants';
import useAnalyticsForToolBar from './analytics/useAnalyticsForToolBar';
import PanelContainer from './panel-container';
import SegmentationPanelIcons from './segmentation-panel-icons';
import { getSAM3TextPrompt, isIncludePointPresent } from './utils';
export const PRE_SELECTED_SIZES = [4, 8, 16, 24, 48];
const Wrapper = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: '10px'
});
const CircleContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: $theme.sizing.scale100,
  borderRadius: $theme.sizing.scale200,
  padding: $theme.sizing.scale100,
  height: '34px',
  width: '34px',
  ':hover': {
    cursor: 'pointer'
  }
}));
const SliderContainer = styled('div', ({
  $theme: {
    sizing
  }
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: sizing.scale300,
  marginRight: sizing.scale300
}));
const sliderOverrides = {
  TickBar: {
    style: {
      display: 'none'
    }
  },
  Thumb: {
    style: ({
      $theme
    }) => ({
      width: $theme.sizing.scale400,
      height: $theme.sizing.scale400
    })
  }
};
export const getAdjustedLocalStrokeValue = value => [Math.round(Math.min(MAX_BRUSH_STROKE_SIZE, value[0]) * 10) / 10];
type PropsT = {
  showPredefinedSizes?: boolean;
};
const SegmentationPanel = ({
  showPredefinedSizes = true
}: PropsT) => {
  const {
    brushToolSettingsAtom,
    isLoadingAtom,
    gridModeAtom,
    snapEnabledAtom,
    dispatchAtom,
    selectedAnnotationLabelAtom,
    currentFrameAnnotationUnderUserReviewAtom
  } = useStageAtoms();
  const {
    selectedTool,
    eraserSelected,
    inclusionExclusion
  } = useAtomValue(brushToolSettingsAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const annotationUnderUserReview = useAtomValue(currentFrameAnnotationUnderUserReviewAtom);
  const gridMode = useAtomValue(gridModeAtom);
  const snapEnabled = useAtomValue(snapEnabledAtom);
  const dispatchAction = useSetAtom(dispatchAtom);
  const isSAM3Enabled = useAtomValue(isSAM3EnabledAtom);
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const selectedClassName = selectedAnnotationLabel ? getSAM3TextPrompt(selectedAnnotationLabel) : '';
  const [localStrokeSize, setLocalStrokeSize] = useState([10]);
  const {
    sendEventToolSelected
  } = useAnalyticsForToolBar();
  const onSizeChange = useCallback((value: number[]) => {
    const adjustedValue = getAdjustedLocalStrokeValue(value);
    setLocalStrokeSize(adjustedValue);
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool,
        eraserSelected,
        size: adjustedValue[0]
      }
    });
  }, [eraserSelected, selectedTool, dispatchAction]);
  const onPredefinedSizeClick = useCallback((value: number[]) => {
    const adjustedValue = getAdjustedLocalStrokeValue(value);
    setLocalStrokeSize(adjustedValue);
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool,
        eraserSelected,
        size: adjustedValue[0]
      }
    });
  }, [selectedTool, eraserSelected, dispatchAction]);
  const onSizeFinalChange = useCallback((value: number[]) => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.SEGMENT_SIZE,
      value: value.toString(),
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
  }, [sendEventToolSelected, gridMode, snapEnabled]);
  const onIncludeRegionClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.SAM_CLICK,
        eraserSelected: false,
        inclusionExclusion: SAM_CLICK.INCLUDE
      }
    });
  }, [dispatchAction]);
  const onExcludeRegionClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.SAM_CLICK,
        eraserSelected: false,
        inclusionExclusion: SAM_CLICK.EXCLUDE
      }
    });
  }, [dispatchAction]);
  const isSAMModelSelected = selectedTool === SEGMENTATION_TOOLS.SAM_BOUNDING_BOX || selectedTool === SEGMENTATION_TOOLS.SAM_CLICK;
  const shouldShowStrokesSlider = !isSAMModelSelected;
  const isSAMClickSelected = selectedTool === SEGMENTATION_TOOLS.SAM_CLICK;
  const shouldDisableExclude = isLoading || !isIncludePointPresent(annotationUnderUserReview);
  return <PanelContainer width={isSAM3Enabled ? 240 : 230}>
      <SegmentationPanelIcons localStrokeSize={localStrokeSize} selectedClassName={selectedClassName} />
      {isSAMClickSelected ? <Wrapper>
          <ToolbarButton title="Click on multiple regions to select them before submitting" onClick={onIncludeRegionClick} attributes={{
        'data-testid': 'sam-click-include'
      }} isSelected={inclusionExclusion === SAM_CLICK.INCLUDE} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isLoading}>
            <CirclePlusFilled title={SAM_CLICK.INCLUDE} />
          </ToolbarButton>
          <ToolbarButton title="Click on regions you wish to exclude before submitting" onClick={onExcludeRegionClick} attributes={{
        'data-testid': 'sam-click-exclude'
      }} isSelected={inclusionExclusion === SAM_CLICK.EXCLUDE} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={shouldDisableExclude}>
            <CircleMinusFilled title={SAM_CLICK.EXCLUDE} />
          </ToolbarButton>
        </Wrapper> : null}
      {showPredefinedSizes && !isSAMModelSelected ? <Wrapper>
          {PRE_SELECTED_SIZES.map(preselectedSize => <CircleContainer key={preselectedSize} data-testid={`circle_${preselectedSize}`} onClick={() => {
        onPredefinedSizeClick([preselectedSize]);
      }}>
              <CircleFilled size={preselectedSize} />
            </CircleContainer>)}
        </Wrapper> : null}
      {shouldShowStrokesSlider && <SliderContainer>
          <Slider step={0.1} value={localStrokeSize} onChange={({
        value
      }) => onSizeChange(value)} onFinalChange={({
        value
      }) => onSizeFinalChange(value)} min={MIN_BRUSH_STROKE_SIZE} max={MAX_BRUSH_STROKE_SIZE} overrides={sliderOverrides} />
        </SliderContainer>}
    </PanelContainer>;
};
export default SegmentationPanel;